import type { IntlShape } from "react-intl";
import { Path, Shape } from "three";
import { type RefinementCtx, ZodIssueCode, type ZodObject, type ZodTypeAny, z } from "zod";
import {
	type ThreeInputLinesType,
	ThreeShapeSideTypes,
	type ThreeSidePathsType,
} from "../../../../../types/three";
import type { DimensionProperties } from "../../../../../types/workpiece";
import type InputType from "../../../Input/InputType";
import { JsonInputType } from "../../../Input/JsonInputType";
import { SideName } from "../../../Side/SideName";
import Triangle from "../Triangle";

export default class Regular extends Triangle {
	public static code = "plate_triangle_regular";

	protected getDimensionSchema(): ZodTypeAny {
		const inputs = super.getDimensionSchema() as ZodObject<Record<string, ZodTypeAny>>;

		return inputs.superRefine((data, context) => {
			const minWidth = Number(this.shapeInput.inputs[0]?.min);
			const maxWidth = Number(this.shapeInput.inputs[0]?.max);
			const totalWidth = Number(data.length_1) + Number(data.length_2);

			if (totalWidth > maxWidth) {
				const issue = {
					code: ZodIssueCode.custom,
					params: {
						type: "combined_too_big",
						field_1: this.shapeInput.inputs.find((input) => input.id === "length_1")?.label,
						field_2: this.shapeInput.inputs.find((input) => input.id === "length_2")?.label,
						maxValue: maxWidth,
						totalValue: totalWidth,
					},
				};
				context.addIssue({ ...issue, path: ["length_1"] });
				context.addIssue({ ...issue, path: ["length_2"] });
			}

			if (totalWidth < minWidth) {
				const issue = {
					code: ZodIssueCode.custom,
					params: {
						type: "combined_too_small",
						field_1: this.shapeInput.inputs.find((input) => input.id === "length_1")?.label,
						field_2: this.shapeInput.inputs.find((input) => input.id === "length_2")?.label,
						minValue: minWidth,
						totalValue: totalWidth,
					},
				};
				context.addIssue({ ...issue, path: ["length_1"] });
				context.addIssue({ ...issue, path: ["length_2"] });
			}
		});
	}

	protected validateBeltSize(data: Record<JsonInputType, number>, context: RefinementCtx): void {
		data.length = Number(data.length_1) + Number(data.length_2);
		data.width = Number(data.width);

		const { biggest, smallest, maxSize, maxSizeInverse } = this.calculateMaxBeltSize(data);

		if (smallest > maxSize) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				params: {
					type: "belt_size_combined_inverse",
					field: this.getInputById(JsonInputType.Width)?.label,
					fields: [
						this.getInputById(JsonInputType.LengthSection1)?.label,
						this.getInputById(JsonInputType.LengthSection2)?.label,
					],
					maxValue: maxSizeInverse,
					value: data.width,
				},
				path: [JsonInputType.LengthSection1],
			});

			context.addIssue({
				code: z.ZodIssueCode.custom,
				params: {
					type: "belt_size_combined_inverse",
					field: this.getInputById(JsonInputType.Width)?.label,
					fields: [
						this.getInputById(JsonInputType.LengthSection1)?.label,
						this.getInputById(JsonInputType.LengthSection2)?.label,
					],
					maxValue: maxSizeInverse,
					value: data.width,
				},
				path: [JsonInputType.LengthSection2],
			});

			context.addIssue({
				code: z.ZodIssueCode.custom,
				params: {
					type: "belt_size_combined",
					field: this.getInputById(JsonInputType.Width)?.label,
					fields: [
						this.getInputById(JsonInputType.LengthSection1)?.label,
						this.getInputById(JsonInputType.LengthSection2)?.label,
					],
					maxValue: maxSize,
					value: biggest,
				},
				path: [JsonInputType.Width],
			});
		}
	}

	public getDimensionsString(input: InputType, intl: IntlShape) {
		if (input.length_1 && input.length_2 && input.width) {
			const { length_1, length_2, width } = this.formatInputs(input, intl, "decimal");

			return `(${length_1} + ${length_2}) x ${width} cm`;
		}
		return undefined;
	}

	public calculateSideLength(side: SideName, input: InputType) {
		const length_1 = Number(input[JsonInputType.LengthSection1]);
		const length_2 = Number(input[JsonInputType.LengthSection2]);
		const height = Number(input[JsonInputType.Height]);

		if (!(Number.isNaN(length_1) || Number.isNaN(length_2) || Number.isNaN(height))) {
			switch (side) {
				case SideName.Basis:
					return length_1 + length_2;
				case SideName.Width1:
					return Math.sqrt(length_1 ** 2 + height ** 2);
				case SideName.Width2:
					return Math.sqrt(length_2 ** 2 + height ** 2);
			}
		}

		return undefined;
	}

	public calculateSheetDimensions(input: InputType): { length: number; width: number } | undefined {
		const length_1 = Number(input[JsonInputType.LengthSection1]);
		const length_2 = Number(input[JsonInputType.LengthSection2]);
		const height = Number(input[JsonInputType.Height]);

		if (!(Number.isNaN(length_1) || Number.isNaN(length_2) || Number.isNaN(height))) {
			return { length: length_1 + length_2, width: height };
		}
	}

	public getThreeShape(props: DimensionProperties["plate_triangle_regular"]) {
		const [length_1, length_2, width] = [
			props.length_1 / 1000,
			props.length_2 / 1000,
			props.width / 1000,
		];

		const length = length_1 + length_2;
		return new Shape().moveTo(0, 0).lineTo(length, 0).lineTo(length_1, width).lineTo(0, 0);
	}

	public getThreeSidePaths(
		props: DimensionProperties["plate_triangle_regular"],
	): ThreeSidePathsType[] {
		const [length_1, length_2, width] = [
			props.length_1 / 1000,
			props.length_2 / 1000,
			props.width / 1000,
		];

		return [
			{
				path: new Path().moveTo(0, 0).lineTo(length_1, 0),
				code: "length1",
				group: "group1",
				type: ThreeShapeSideTypes.side,
			},
			{
				path: new Path().moveTo(length_1, 0).lineTo(length_2 + length_1, 0),
				code: "length2",
				group: "group1",
				type: ThreeShapeSideTypes.side,
			},
			{
				path: new Path().moveTo(length_1 + length_2, 0).lineTo(length_1, width),
				code: "width2",
				type: ThreeShapeSideTypes.side,
			},
			{
				path: new Path().moveTo(length_1, width).lineTo(0, 0),
				code: "width1",
				type: ThreeShapeSideTypes.side,
			},
		];
	}

	public getThreeInputLines(
		props: DimensionProperties["plate_triangle_regular"],
	): ThreeInputLinesType[] {
		const [length_1, length_2, width] = [
			props.length_1 / 1000,
			props.length_2 / 1000,
			props.width / 1000,
		];

		return [
			{
				dimension: props.length_1,
				code: "length_1",
				path: new Path().moveTo(0, 0).lineTo(length_1, 0),
				type: ThreeShapeSideTypes.side,
			},
			{
				dimension: props.length_2,
				code: "length_2",
				path: new Path().moveTo(length_1, 0).lineTo(length_1 + length_2, 0),
				type: ThreeShapeSideTypes.side,
			},
			{
				dimension: props.width,
				code: "width",
				path: new Path().moveTo(0, 0).lineTo(0, width),
				type: ThreeShapeSideTypes.side,
			},
		];
	}

	public getDimensions(props: DimensionProperties["plate_triangle_regular"]) {
		return {
			width: props.width ? props.width : 1000,
			length_1: props.length_1 ? props.length_1 : 500,
			length_2: props.length_2 ? props.length_2 : 1500,
		};
	}
}
